<template>
	<a class="site-logo" :href="href" :class="{ 'site-logo--text': !logoSrc }" :style="rootStyle" :aria-label="name">
		<img v-if="logoSrc" class="site-logo__img" v-bind="logoAttrs" :alt="name" :style="imgStyle" />
		<span v-else class="site-logo__name" :style="nameStyle">{{ name }}</span>
	</a>
</template>

<script>
/**
 * SiteLogoBlock.vue
 * ─────────────────
 * The site brand mark. Reads the logo and site name from the injected
 * `siteSettings` (Site Design → Brand), so one change there updates every
 * header and footer. Shows the logo image when set, otherwise the site name
 * as text in the heading font. Never stores the logo URL in page content.
 *
 * Alignment comes from the Layout trait on the wrapper; this block only
 * sizes itself.
 */
import { imgAttrs } from '../shared/imageSrc.js';

export default {
	name: 'SiteLogoBlock',

	inject: {
		siteSettings: { default: function () { return {}; } }
	},

	props: {
		maxHeight: { type: String, default: '48px' },
		maxWidth: { type: String, default: '' },
		linkToHome: { type: [Boolean, String], default: true },
		textSize: { type: String, default: '1.75em' },
		textWeight: { type: String, default: '800' }
	},

	computed: {
		/** Logos are small and above the fold: eager, 320/640 variants only. */
		logoAttrs: function () {
			return imgAttrs(this.logoSrc, { priority: true, widths: [320, 640], fallbackWidth: 640, sizes: '320px' });
		},

		logoSrc: function () { return (this.siteSettings && this.siteSettings.logoSrc) || ''; },
		name: function () { return (this.siteSettings && this.siteSettings.siteName) || 'Your publication'; },
		href: function () { return this.isOn(this.linkToHome) ? '/' : null; },

		rootStyle: function () {
			return { cursor: this.href ? 'pointer' : 'default' };
		},

		imgStyle: function () {
			var s = { maxHeight: this.maxHeight || '48px' };
			if (this.maxWidth) s.maxWidth = this.maxWidth;
			return s;
		},

		nameStyle: function () {
			return { fontSize: this.textSize || '1.75em', fontWeight: this.textWeight || '800' };
		}
	},

	methods: {
		isOn: function (v) { return v === true || v === 'true' || v === 1 || v === '1'; }
	}
};
</script>

<style scoped>
.site-logo { display: inline-flex; align-items: center; max-width: 100%; text-decoration: none; color: inherit; line-height: 1; }
.site-logo__img { display: block; width: auto; max-width: 100%; height: auto; }
.site-logo__name {
	font-family: var(--site-heading-font, inherit);
	color: var(--site-heading-color, inherit);
	letter-spacing: -0.02em;
	white-space: nowrap;
}
</style>
